/**
 * The school's own clock. Every moment the API returns is UTC (ISO 8601 with a "Z"), and what "today", "9am" or "10 Oct" means depends on the
 * institution's time zone (set by the super admin) — not on whichever browser happens to be open. Everything that shows a moment, or asks
 * "what day is it?", goes through here.
 *
 * Plain calendar dates ("2026-10-07") have no zone and are never shifted.
 */
import { DEFAULT_TIMEZONE, isValidTimezone } from "@/lib/timezones";
import { appLocale } from "@/lib/appLocale";

const STORAGE_KEY = "schoolTimezone";

function readStored(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && isValidTimezone(stored) ? stored : null;
  } catch {
    return null;
  }
}

let schoolTimezone: string = readStored() ?? DEFAULT_TIMEZONE;

/** Called wherever the institution's zone becomes known (login, branding, the teacher/student profile). Ignores empty or unknown values. */
export function setSchoolTimezone(timeZone: string | null | undefined): void {
  if (!timeZone || !isValidTimezone(timeZone) || timeZone === schoolTimezone) return;
  schoolTimezone = timeZone;
  try {
    localStorage.setItem(STORAGE_KEY, timeZone);
  } catch {
    /* storage unavailable — the in-memory value still applies */
  }
}

export const getSchoolTimezone = (): string => schoolTimezone;

const isDateOnly = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value);

/** Any API moment (ISO string, DB text, Date) as a Date. */
function toDate(value: string | number | Date): Date {
  if (value instanceof Date) return value;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(value)) return new Date(`${value.replace(" ", "T")}Z`);
  return new Date(value);
}

type Kind = "date" | "time" | "datetime";

/**
 * Formats a moment on the school's clock — a drop-in for `new Date(x).toLocaleString(locale, options)` and its Date/Time variants.
 * A plain date is formatted as that date (UTC), so it can't slip a day.
 */
export function inSchool(value: string | number | Date | null | undefined, kind: Kind, locale?: string | string[], options?: Intl.DateTimeFormatOptions): string {
  if (value === null || value === undefined || value === "") return "—";
  const dateOnly = typeof value === "string" && isDateOnly(value);
  const date = dateOnly ? new Date(`${value}T00:00:00Z`) : toDate(value);
  if (Number.isNaN(date.getTime())) return String(value);
  if (locale === undefined || locale === "en-IN" || locale === "en") locale = appLocale();
  const withZone: Intl.DateTimeFormatOptions = { ...options, timeZone: dateOnly ? "UTC" : options?.timeZone ?? schoolTimezone };
  if (kind === "date") return date.toLocaleDateString(locale, withZone);
  if (kind === "time") return date.toLocaleTimeString(locale, withZone);
  return date.toLocaleString(locale, withZone);
}

/** The calendar date (YYYY-MM-DD) it is now — or a given moment is — at the school. */
export function schoolToday(now: Date = new Date()): string {
  return now.toLocaleDateString("en-CA", { timeZone: schoolTimezone });
}

/**
 * A Date whose *local* year/month/day/hour/minute read the school's wall clock right now. For older code that builds calendars and
 * greetings from `getFullYear()/getMonth()/getDate()/getHours()` — hand it this instead of `new Date()`. Not a real instant: don't compare or send it.
 */
export function schoolNow(now: Date = new Date()): Date {
  const [y, m, d] = schoolToday(now).split("-").map(Number);
  const [hh, mm, ss] = now.toLocaleTimeString("en-GB", { timeZone: schoolTimezone, hour12: false }).split(":").map(Number);
  return new Date(y, m - 1, d, hh % 24, mm, ss);
}

/** The school day (YYYY-MM-DD) a moment falls on — use instead of `iso.slice(0, 10)`, which is the UTC date. */
export function schoolDay(value: string | Date): string {
  if (typeof value === "string" && isDateOnly(value)) return value;
  return toDate(value).toLocaleDateString("en-CA", { timeZone: schoolTimezone });
}

/** A calendar date shifted by whole days (pure calendar arithmetic). */
export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** How far ahead of UTC a zone's clock is at a given moment, in minutes. */
function offsetMinutes(timeZone: string, at: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(at);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000);
}

/** What a `datetime-local` input holds ("2026-10-12T10:00") is the school's wall clock; this turns it into the exact moment (ISO UTC) to send. */
export function schoolLocalToIso(local: string | null | undefined): string | null {
  if (!local) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!match) return null;
  const [, y, mo, d, h, mi] = match.map(Number) as unknown as number[];
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi);
  let instant = wallAsUtc - offsetMinutes(schoolTimezone, new Date(wallAsUtc)) * 60000;
  const corrected = wallAsUtc - offsetMinutes(schoolTimezone, new Date(instant)) * 60000; // across a daylight-saving change one correction settles it
  if (corrected !== instant) instant = corrected;
  return new Date(instant).toISOString();
}

/** The value to put in a `datetime-local` input for a stored moment — its school wall-clock time. */
export function isoToSchoolLocal(value: string | null | undefined): string {
  if (!value) return "";
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: schoolTimezone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}


/** "19:08" → "7:08 PM". Anything that is not HH:MM is returned as it came. */
export function time12(hhmm: string | null | undefined): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm ?? ""));
  if (!m) return String(hhmm ?? "");
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h >= 12 ? "PM" : "AM"}`;
}

/** "2026-10-27" → "27 Oct 2026" (or "27 Oct" with `withYear: false`). */
export function dayLabel(iso: string | null | undefined, withYear = true): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ""));
  if (!m) return String(iso ?? "");
  const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(m[2]) - 1];
  return `${Number(m[3])} ${month}${withYear ? ` ${m[1]}` : ""}`;
}
